// frontend/src/components/EmpresaPortal/ConfiguracionMenu.js
// Menú de configuración del Portal de Empresa: información básica del perfil
// (nombre, correo, empresa) y la preferencia de correos de producto /
// marketing (usuarios.recibe_boletin, PUT /api/auth/preferencias). Es la
// base sobre la que se irán agregando más opciones (seguridad, etc.).

import React, { useState, useRef, useEffect } from 'react';
import { Settings, X, Mail, Building2 } from 'lucide-react';
import './ConfiguracionMenu.css';

const iniciales = (nombre) => {
  if (!nombre) return '?';
  const partes = nombre.trim().split(/\s+/);
  return partes.slice(0, 2).map(p => p[0]?.toUpperCase() || '').join('') || '?';
};

const ConfiguracionMenu = ({ usuario, empresaNombre }) => {
  const [abierto, setAbierto] = useState(false);
  const [recibeBoletin, setRecibeBoletin] = useState(Boolean(usuario?.recibe_boletin));
  const [guardando, setGuardando] = useState(false);
  const [errorBoletin, setErrorBoletin] = useState('');
  // Al desactivar el boletín se pregunta el motivo antes de guardar
  const [preguntandoBaja, setPreguntandoBaja] = useState(false);
  const [motivos, setMotivos] = useState([]);
  const [motivoBaja, setMotivoBaja] = useState('');
  const [comentarioBaja, setComentarioBaja] = useState('');
  const contenedorRef = useRef(null);

  // usuario llega después del primer render (lo trae /api/auth/verificar)
  useEffect(() => {
    setRecibeBoletin(Boolean(usuario?.recibe_boletin));
  }, [usuario?.recibe_boletin]);

  const guardarBoletin = async (valor, extra = {}) => {
    setRecibeBoletin(valor);
    setGuardando(true);
    setErrorBoletin('');
    try {
      const response = await fetch('/api/auth/preferencias', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recibe_boletin: valor, ...extra })
      });
      if (!response.ok) throw new Error();
      setPreguntandoBaja(false);
    } catch {
      setRecibeBoletin(!valor);
      setErrorBoletin('No se pudo guardar el cambio. Intenta de nuevo.');
    } finally {
      setGuardando(false);
    }
  };

  const cambiarBoletin = async (valor) => {
    if (valor) return guardarBoletin(true);
    setMotivoBaja('');
    setComentarioBaja('');
    setErrorBoletin('');
    setPreguntandoBaja(true);
    if (motivos.length === 0) {
      try {
        const response = await fetch('/api/boletines/motivos-baja');
        if (response.ok) setMotivos(await response.json());
      } catch { /* sin catálogo se puede dar de baja igual, solo sin motivo */ }
    }
  };

  const confirmarBaja = () => guardarBoletin(false, { motivo: motivoBaja || null, comentario: comentarioBaja });

  useEffect(() => {
    const handleClickFuera = (e) => {
      if (contenedorRef.current && !contenedorRef.current.contains(e.target)) {
        setAbierto(false);
      }
    };
    document.addEventListener('mousedown', handleClickFuera);
    return () => document.removeEventListener('mousedown', handleClickFuera);
  }, []);

  return (
    <div className="cfg-container" ref={contenedorRef}>
      <button type="button" className="cfg-trigger" onClick={() => setAbierto(v => !v)} title="Configuración" data-tour="configuracion">
        <Settings size={18} />
      </button>

      {abierto && (
        <div className="cfg-panel">
          <div className="cfg-panel-header">
            <strong>Configuración</strong>
            <button type="button" className="cfg-close" onClick={() => setAbierto(false)} aria-label="Cerrar">
              <X size={16} />
            </button>
          </div>

          <div className="cfg-perfil">
            <div className="cfg-avatar">{iniciales(usuario?.nombre)}</div>
            <div className="cfg-perfil-info">
              <strong>{usuario?.nombre || 'Usuario'}</strong>
              <span className="cfg-perfil-rol">Administrador de empresa</span>
            </div>
          </div>

          <div className="cfg-detalle">
            <div className="cfg-detalle-item">
              <Mail size={15} />
              <span>{usuario?.email || 'Sin correo'}</span>
            </div>
            <div className="cfg-detalle-item">
              <Building2 size={15} />
              <span>{empresaNombre || 'Sin empresa'}</span>
            </div>
          </div>

          <div className="cfg-seccion">
            <span className="cfg-seccion-titulo">Correos</span>
            <label className="cfg-toggle">
              <span className="cfg-toggle-texto">
                <strong>Actualizaciones y novedades</strong>
                <small>Noticias del producto, nuevas funciones, promociones y boletín de PsyTest.</small>
              </span>
              <input
                type="checkbox"
                role="switch"
                checked={recibeBoletin}
                onChange={(e) => cambiarBoletin(e.target.checked)}
                disabled={guardando || preguntandoBaja}
              />
              <span className="cfg-switch" aria-hidden="true" />
            </label>

            {preguntandoBaja && (
              <div className="cfg-baja">
                <strong>¿Nos cuentas por qué? <span>(opcional)</span></strong>
                <div className="cfg-baja-opciones" role="radiogroup">
                  {motivos.map(m => (
                    <label key={m.clave} className={motivoBaja === m.clave ? 'activo' : ''}>
                      <input
                        type="radio"
                        name="motivo-baja"
                        value={m.clave}
                        checked={motivoBaja === m.clave}
                        onChange={() => setMotivoBaja(m.clave)}
                        disabled={guardando}
                      />
                      {m.texto}
                    </label>
                  ))}
                </div>
                <textarea
                  rows={2}
                  maxLength={1000}
                  value={comentarioBaja}
                  onChange={(e) => setComentarioBaja(e.target.value)}
                  placeholder="Comentarios adicionales (opcional)"
                  disabled={guardando}
                />
                <div className="cfg-baja-acciones">
                  <button type="button" className="cfg-btn secundario" onClick={() => setPreguntandoBaja(false)} disabled={guardando}>
                    Cancelar
                  </button>
                  <button type="button" className="cfg-btn primario" onClick={confirmarBaja} disabled={guardando}>
                    {guardando ? 'Guardando...' : 'Darme de baja'}
                  </button>
                </div>
              </div>
            )}

            {errorBoletin && <p className="cfg-error">{errorBoletin}</p>}
          </div>

          <p className="cfg-proximamente">Más opciones de configuración estarán disponibles próximamente.</p>
        </div>
      )}
    </div>
  );
};

export default ConfiguracionMenu;
